import React, { useState, useEffect } from 'react';
import Header from '../components/header';
import './updateStockDetails.css';
import InputField from '../components/inputControls/inputField';
import { formatCycleInfoDate, getCalendarInfo } from '../helper-functions';
import { put } from '../config/api';
import { UPDATE_STOCK_TITLES, UPDATE_STOCK_MESSAGES } from '../constants/textconstants';
import { useGlobalSnackbar } from '../components/snackbar/SnackbarProvider';
import { RequestStockData } from '../interfaces/types';

interface StockItem {
  productName: string;
  productDisplayName?: string;
  stockQuantity: string;
}

interface UpdateStockDetailsProps {
  stockData?: RequestStockData[];
  selectedDate?: string;
  onBack?: () => void;
  onSave?: (updatedStockData: StockItem[]) => void;
  onNavigateToHome?: () => void;
  onNavigateToCCMHome?: () => void;
  loggedInUser?: string;
  userRole?: string;
  customerId?: string;
}

export const UpdateStockDetails: React.FC<UpdateStockDetailsProps> = ({ 
  stockData = [],
  selectedDate = '',
  onBack,
  onSave,
  onNavigateToHome,
  onNavigateToCCMHome,
  userRole,
  customerId
}) => {

  // Debug: Log the received customerId
  console.log('UpdateStockDetails received customerId:', customerId);

  // Get calendar info for due date
  const calendarInfo = getCalendarInfo();

  const [editedStockData, setEditedStockData] = useState<StockItem[]>(() => {
    console.log('Received stock data:', stockData);
    if (stockData.length === 0) {
      console.log('No stock data provided, using empty array');
      return [];
    }
    return stockData.map(item => ({ 
      productName: item.productName, 
      productDisplayName: item.productDisplayName || item.productName,
      stockQuantity: String(item.stockQuantity || 0)  // Convert incoming number to string
    }));
  });

  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [isSaving, setIsSaving] = useState(false);

  // Snackbar initialize
  const { showSnackbar } = useGlobalSnackbar();

  useEffect(() => {
    console.log('Stock data prop changed:', stockData);
    if (stockData.length > 0) {
      setEditedStockData(stockData.map(item => ({ 
        productName: item.productName, 
        productDisplayName: item.productDisplayName || item.productName,
        stockQuantity: String(item.stockQuantity || 0)  // Convert incoming number to string
      })));
    }
  }, [stockData]);

  const handleBackClick = () => {
    if (onBack) {
      onBack();
    } else {
      console.log('Back to previous page');
    }
  };

  const handleQuantityChange = (id: string, newQuantity: string) => {
    // Allow only integers (remove any non-digit characters including decimals)
    const cleanedValue = newQuantity.replace(/[^0-9]/g, '');
    
    // Check if value exceeds maximum limit of 20000
    const numericValue = parseInt(cleanedValue, 10);
    if (!isNaN(numericValue) && numericValue > 20000) {
      return; // Don't update if value exceeds 20000
    }
    
    // Clear validation error for this field when user starts typing
    if (validationErrors[id]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[id];
        return newErrors;
      });
    }
    
    setEditedStockData(prev => 
      prev.map(item => 
        item.productName === id ? { ...item, stockQuantity: cleanedValue } : item
      )
    );
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};
    let isValid = true;

    editedStockData.forEach(item => {
      if (!item.stockQuantity || item.stockQuantity.trim() === '') {
        errors[item.productName] = UPDATE_STOCK_MESSAGES.THIS_FIELD_REQUIRED;
        isValid = false;
      } else {
        const numericValue = parseInt(item.stockQuantity, 10);
        if (isNaN(numericValue) || numericValue < 0) {
          errors[item.productName] = UPDATE_STOCK_MESSAGES.QUANTITY_POSITIVE_NUMBER;
          isValid = false;
        } else if (numericValue > 20000) {
          errors[item.productName] = 'Quantity cannot exceed 20000 kg';
          isValid = false;
        }
      }
    });

    setValidationErrors(errors);
    return isValid;
  };

  const handleSave = async () => {
    // Validate form before saving
    if (!validateForm()) {
      alert(UPDATE_STOCK_MESSAGES.FILL_REQUIRED_FIELDS);
      return;
    }

    setIsSaving(true);

    try {
      // Use customerId prop if provided (from CCM flow), otherwise get from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const customerIdToUse = customerId || urlParams.get('userId')?.toUpperCase(); // Fallback ID if not found
      
      // Prepare request body
      const requestBody = {
        stockCycle: selectedDate || new Date().toISOString().split('T')[0], // Use selected date or current date
        productStocks: editedStockData.map(item => ({
          productName: item.productName,
          stockQuantity: parseInt(item.stockQuantity, 10) // Convert string to number for API
        }))
      };

      console.log('Sending request to API:', requestBody);
      console.log('Using customer ID:', customerIdToUse);

      // Send to API
      const response = await put(`/api/v1/customers/${customerIdToUse}/product-stocks`, requestBody);
      
      if (response) {
        console.log('API response:', response);
        
        // Save the data to parent component
        if (onSave) {
          onSave(editedStockData);
        }
        
        console.log('Saving updated stock data:', editedStockData);
        
        // Show success message
        showSnackbar(UPDATE_STOCK_MESSAGES.STOCK_SAVED_SUCCESSFULLY, 'default');

        // Navigate based on user role
        if (userRole === 'retailer') {
          // For retailers, navigate to home with data refresh
          if (onNavigateToHome) {
            console.log('Navigating to retailer home with data refresh');
            onNavigateToHome();
          } else if (onBack) {
            onBack();
          }
        } else if (userRole === 'ccm') {
          // For CCM users, navigate to CCM home with data refresh
          if (onNavigateToCCMHome) {
            console.log('Navigating to CCM home with data refresh');
            onNavigateToCCMHome();
          } else if (onBack) {
            onBack();
          }
        } else {
          // Fallback navigation
          if (onNavigateToHome) {
            onNavigateToHome();
          } else if (onBack) {
            onBack();
          }
        }
      }
    } catch (error) {
      console.error('Error saving stock data:', error);
      alert(UPDATE_STOCK_MESSAGES.SAVE_FAILED);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="page-container update-stock-page-container">
      {/* Header Section */}
      <Header title={UPDATE_STOCK_TITLES.UPDATE_STOCK_DETAILS} onBackClick={handleBackClick} />

      <div className="update-stock-content-section" >
        {/* Information details */}
        <div className="information-section">
          <h3 className="information-title">{UPDATE_STOCK_TITLES.STOCK_DETAILS_AS_OF} {formatCycleInfoDate(selectedDate || undefined, 'DD MMM')}</h3>
        </div>

        {/* Update Stock Alert */}
        <div className="stock-update-alert">
          <span className="alert-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M10 0C4.5 0 0 4.5 0 10C0 15.5 4.5 20 10 20C15.5 20 20 15.5 20 10C20 4.5 15.5 0 10 0ZM10 18C5.59 18 2 14.41 2 10C2 5.59 5.59 2 10 2C14.41 2 18 5.59 18 10C18 14.41 14.41 18 10 18ZM10.5 5H9V11L14.2 14.2L15 12.9L10.5 10.2V5Z"
                fill="#E65100"
              />
            </svg>
          </span>
          <span className="stock-alert-text">
            {userRole === 'retailer'
              ? `${UPDATE_STOCK_MESSAGES.YOU_CAN_ADD_EDIT_RETAILER} ${formatCycleInfoDate(calendarInfo?.currentCycleRetailerDueDate, 'DD MMM YYYY')}` 
              : `${UPDATE_STOCK_MESSAGES.YOU_CAN_ADD_EDIT_CCM} ${formatCycleInfoDate(calendarInfo?.currentCycleDueDate, 'DD MMM YYYY')}`
            }
          </span>
        </div>

        {/* Stock Items List */}
        <div className="stock-items-section">
          <div className="stock-items-list">
            {editedStockData.length === 0 ? (
              <div className="no-stock-data">
                <p>{UPDATE_STOCK_MESSAGES.NO_STOCK_DATA_RETAILER}</p>
              </div>
            ) : (
              editedStockData.map((item, index) => (
                <div key={item.productName} className="stock-item-card">
                  <div className="stock-item-info">
                    <span className="stock-item-id">
                      {item.productDisplayName || item.productName} <span>*</span>
                    </span>
                  </div>
                  <div className="quantity-input-section">
                    <div className="input-row">
                      <InputField
                        placeholder={UPDATE_STOCK_MESSAGES.ENTER_HERE}
                        value={item.stockQuantity}
                        onChange={(e) =>
                          handleQuantityChange(item.productName, e.target.value)
                        }
                        type="text"
                        min="0"
                        required={true}
                        inputMode="numeric"
                      />
                      <span className="quantity-unit">{UPDATE_STOCK_MESSAGES.KGS}</span>
                    </div>
                    {validationErrors[item.productName] && (
                      <div className="error-message">
                        {validationErrors[item.productName]}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Footer with Save button */}
      <div className="save-footer">
        <button className="save-btn" onClick={handleSave} disabled={isSaving}>
          {UPDATE_STOCK_TITLES.SAVE_DETAILS}
        </button>
      </div>
    </div>
  );
};

export default UpdateStockDetails;
